import React, { useState } from 'react';
import { Wind, Users, Heart, TrendingUp, ChevronDown, ChevronUp } from 'lucide-react';
import { OxygenMetrics, formatIndianNumber, formatCurrency } from '../../utils/environmentalCalculations';
import { NatureCard } from '../shared/NatureCard';
import { MetricDisplay } from '../shared/MetricDisplay';
import { BotanicalDivider } from '../shared/BotanicalDivider';
import './OxygenSection.css';

interface OxygenSectionProps {
  metrics: OxygenMetrics;
}

export const OxygenSection: React.FC<OxygenSectionProps> = ({ metrics }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <section className="oxygen-section">
      <div className="oxygen-section__container">
        <div
          className="oxygen-section__header"
          onClick={() => setIsExpanded(!isExpanded)}
          style={{ cursor: 'pointer' }}
        >
          <Wind size={48} className="oxygen-section__header-icon" />
          <div style={{ flex: 1 }}>
            <h2 className="oxygen-section__title">Oxygen Production</h2>
            <p className="oxygen-section__subtitle">
              Annual oxygen generation capacity and public health impact
            </p>
          </div>
          {isExpanded ? <ChevronUp size={32} /> : <ChevronDown size={32} />}
        </div>

        {isExpanded && (
          <>

        <BotanicalDivider style="simple" spacing="medium" />

        {/* Contextual Information */}
        <div className="oxygen-section__context">
          <div className="oxygen-section__context-card">
            <div className="oxygen-section__context-icon">🌳</div>
            <div className="oxygen-section__context-content">
              <strong>Trees per Person:</strong> {metrics.treesPerPerson.toFixed(1)} mature trees
              required to support one person's annual oxygen needs
            </div>
          </div>
        </div>

        <BotanicalDivider style="leaf" spacing="medium" />

        {/* Primary Metrics */}
        <div className="oxygen-section__primary">
          <NatureCard className="oxygen-section__hero-card" gradient="forest">
            <div className="oxygen-section__hero-value">
              {formatIndianNumber(metrics.totalOxygenLiters)}
            </div>
            <div className="oxygen-section__hero-label">Liters of Oxygen Annually</div>
            <div className="oxygen-section__hero-sublabel">
              {metrics.totalOxygenTonnes.toFixed(2)} tonnes O₂/year
            </div>
          </NatureCard>
        </div>

        {/* Impact Metrics Grid */}
        <div className="oxygen-section__grid">
          <MetricDisplay
            value={metrics.peopleSupported}
            label="People Supported"
            icon={<Users size={32} />}
            comparison="Annual oxygen requirement (287 kg/person)"
            color="forest"
            size="medium"
            delay={0.1}
          />

          <MetricDisplay
            value={metrics.medicalCylinders.toLocaleString('en-IN')}
            label="Medical Cylinders"
            icon={<Heart size={32} />}
            comparison="680-liter standard cylinders"
            color="red"
            size="medium"
            delay={0.2}
          />

          <MetricDisplay
            value={metrics.cityContributionPct.toFixed(4)}
            unit="%"
            label="City Contribution"
            icon={<TrendingUp size={32} />}
            comparison="Of Pune's total oxygen requirement"
            color="blue"
            size="medium"
            delay={0.3}
          />
        </div>

        {/* Economic Valuation */}
        <NatureCard className="oxygen-section__economic">
          <h3 className="oxygen-section__section-title">Public Health Infrastructure Value</h3>
          <p className="oxygen-section__description">
            Economic equivalent based on medical oxygen cylinder market rates (₹500-1000 per cylinder)
          </p>

          <div className="oxygen-section__value-range">
            <div className="oxygen-section__value-item">
              <div className="oxygen-section__value-label">Conservative</div>
              <div className="oxygen-section__value-amount">
                {formatCurrency(metrics.healthValueMin)}
              </div>
            </div>

            <div className="oxygen-section__value-divider">to</div>

            <div className="oxygen-section__value-item">
              <div className="oxygen-section__value-label">Optimistic</div>
              <div className="oxygen-section__value-amount">
                {formatCurrency(metrics.healthValueMax)}
              </div>
            </div>
          </div>

          <div className="oxygen-section__value-average">
            Average Annual Value: <strong>{formatCurrency(metrics.healthValueAvg)}</strong>
          </div>
        </NatureCard>
        </>
        )}
      </div>
    </section>
  );
};

export default OxygenSection;
